import type { RefObject } from 'react';
import { CompanySwitcher } from '../components/company/CompanySwitcher';
import { CurrentUser } from './CurrentUser';
import { useTheme } from '../providers/ThemeProvider';
import { isPreviewMode } from '../lib/preview/flag';
import { MenuIcon, SunIcon, MoonIcon, Tooltip } from '../ui';

// App shell top bar (foundation): company switcher, theme toggle, sign-out. Notification
// Center / Alert / Task / Reminder badges are scoped to the design system (Phase 6.2) —
// their mount point is here; the components themselves are not built yet.
export function TopBar({
  onToggleNav,
  navOpen,
  navId,
  hamburgerRef,
}: {
  onToggleNav: () => void;
  navOpen: boolean;
  /** Same id AppShell passes to Sidebar — the single source of truth for aria-controls. */
  navId: string;
  /** Lets AppShell restore focus here after any nav dismissal (backdrop/Escape/link). */
  hamburgerRef: RefObject<HTMLButtonElement>;
}): JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const themeToggleLabel = theme === 'dark' ? 'ღია რეჟიმზე გადართვა' : 'მუქ რეჟიმზე გადართვა';

  return (
    <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-2 min-[480px]:min-h-14 min-[480px]:py-0 md:h-auto md:py-2 xl:min-h-14 xl:py-0">
      <button
        ref={hamburgerRef}
        type="button"
        onClick={onToggleNav}
        className="flex h-11 w-11 items-center justify-center rounded-token border border-border text-fg md:hidden"
        aria-label="ნავიგაცია"
        aria-expanded={navOpen}
        aria-controls={navId}
      >
        <MenuIcon className="h-5 w-5" />
      </button>
      {isPreviewMode() && (
        <span
          className="min-w-0 max-w-[7rem] shrink truncate whitespace-nowrap rounded-full bg-warning/20 px-2 py-0.5 text-xs font-semibold text-warning min-[480px]:max-w-none"
          role="status"
        >
          {/* Tablet (768–1279px) shows a compact "დემო" badge; mobile and the approved
              desktop (≥1280px) keep the full wording — a CSS-only swap via display, both
              spans always present so screen readers never see stale/duplicated text. */}
          <span className="md:hidden xl:inline">PREVIEW MODE · დემო მონაცემები</span>
          <span className="hidden md:inline xl:hidden">დემო</span>
        </span>
      )}
      <div className="flex-1" />
      {/* Below 768px and again at the approved 1280px+ desktop, the switcher sits inline;
          in the 768–1279px tablet gap it drops to its own full-width row (mirrors the
          mobile order-last/basis-full pattern) so it's never squeezed by the sidebar. */}
      <div className="order-last min-w-0 max-w-full basis-full min-[480px]:order-none min-[480px]:basis-auto md:order-last md:basis-full xl:order-none xl:basis-auto">
        <CompanySwitcher />
      </div>
      {/* Slice P0-H (D1/D3) — who is signed in, as an avatar + name + role menu. "გასვლა" lives
          inside that menu now: it is an account action, and as a peer of the theme toggle it read
          as one more setting. Three ranks remain, left to right: company → user → theme. */}
      <CurrentUser />
      {/* Placeholder mount for the Phase 6.2 Notification Center / badges. */}
      {/* Slice T1 — the icon and the label state the TARGET mode (what one click gets you), not
          the current one: in light mode a moon offering dark, in dark mode a sun offering light.
          The button previously showed the mode it was already in, which reads as a status
          indicator rather than a control. Tooltip and aria-label carry the same sentence, so
          pointer and screen-reader users are told exactly the same thing.
          D2: borderless — a preference, not an account control. The 44px touch target stays. */}
      <Tooltip content={themeToggleLabel}>
        <button
          type="button"
          onClick={toggleTheme}
          className="flex h-11 w-11 items-center justify-center rounded-token text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
          aria-label={themeToggleLabel}
        >
          {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
        </button>
      </Tooltip>
    </header>
  );
}
